import React, { useState, useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { debounce } from 'lodash';
import {
    searchAssetsExternalSystem,
    getTagCloudExternalSystem,
    ExternalSystemSearchRequest,
    ExternalSystemTagCloudRequest,
    ExternalSystemTagCloudResponse,
    ExternalSystemAssetResponse,
    ExternalSystemTagCloudItem
} from '../api/externalSystemApi';
import { AssetMetadata } from '../types';
import AssetCard from '../components/AssetCard';
import { Pagination } from '../components/Pagination';
import { PAGINATION_CONFIG, getDefaultPageSize, getAvailablePageSizes } from '../config/pagination';

// Base64 encoded placeholder image
const PLACEHOLDER_IMAGE = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI2VlZWVlZSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjQiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5ObyBJbWFnZTwvdGV4dD48L3N2Zz4=';

interface SearchPageProps {
    projectId?: string;
    onSelect?: (asset: AssetMetadata) => void;
    isComponent?: boolean;
}

// Styled components
const Container = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 24px;
`;

const SearchContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-bottom: 2rem;
    width: 100%;
`;

const SearchInput = styled.input`
    padding: 0.75rem 1rem;
    font-size: 1rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    width: 100%;
    transition: border-color 0.2s;

    &:focus {
        outline: none;
        border-color: #007bff;
    }
`;

const SearchButton = styled.button`
    padding: 0.75rem 1rem;
    font-size: 1rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    background-color: #007bff;
    color: white;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
        background-color: #0056b3;
    }
`;

const ResultsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 16px;
`;

const Loading = styled.div`
    text-align: center;
    padding: 24px;
    color: #666;
`;

const ErrorMessage = styled.div`
    color: #dc3545;
    padding: 16px;
    background-color: #f8d7da;
    border-radius: 4px;
    margin-bottom: 16px;
`;

const FilterContainer = styled.div`
    background: #f8f9fa;
    border-radius: 4px;
    padding: 1rem;
    width: 100%;
`;

const FilterHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    margin-bottom: 1rem;
`;

const FilterTitle = styled.h3`
    margin: 0;
    font-size: 1rem;
    color: #333;
`;

const FilterContent = styled.div<{ isOpen: boolean }>`
    display: ${props => props.isOpen ? 'block' : 'none'};
`;

const FilterRow = styled.div`
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
    margin-bottom: 1.0rem;
`;

const FilterGroup = styled.div`
    flex: 1;
    min-width: 200px;
`;

const FilterLabel = styled.label`
    display: block;
    margin-bottom: 0.5rem;
    font-size: 0.875rem;
    color: #666;
`;

const FilterInput = styled.input`
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 0.875rem;
    transition: border-color 0.2s;

    &:focus {
        outline: none;
        border-color: #007bff;
    }
`;

const FilterSelect = styled.select`
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 0.875rem;
    transition: border-color 0.2s;

    &:focus {
        outline: none;
        border-color: #007bff;
    }
`;

const FilterButton = styled.button`
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    background-color: #007bff;
    color: white;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 8px;

    &:hover {
        background-color: #0056b3;
    }

    &:disabled {
        background-color: #ccc;
        cursor: not-allowed;
    }
`;

const FilterActions = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #eee;
`;

const TagCloudContainer = styled.div`
    background: white;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 16px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const TagCloudTitle = styled.h3`
    margin: 0 0 12px 0;
    font-size: 1rem;
    color: #333;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const TagCloudGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const TagCloudTag = styled.button<{ isSelected: boolean; tagType?: string }>`
    display: inline-flex;
    align-items: center;
    padding: 6px 12px;
    border-radius: 16px;
    font-size: 0.85rem;
    border: 1px solid;
    cursor: pointer;
    transition: all 0.2s;
    position: relative;
    
    ${props => {
        if (props.isSelected) {
            return `
                background-color: #007bff;
                color: white;
                border-color: #007bff;
            `;
        }

        switch (props.tagType) {
            case 'folder':
                return `
                    background-color: #e3f2fd;
                    color: #1976d2;
                    border-color: #bbdefb;
                    &:hover {
                        background-color: #bbdefb;
                    }
                `;
            case 'ai':
                return `
                    background-color: #f3e5f5;
                    color: #7b1fa2;
                    border-color: #e1bee7;
                    &:hover {
                        background-color: #e1bee7;
                    }
                `;
            case 'unified':
                return `
                    background-color: #e8f5e8;
                    color: #2e7d32;
                    border-color: #c8e6c9;
                    &:hover {
                        background-color: #c8e6c9;
                    }
                `;
            default:
                return `
                    background-color: #f5f5f5;
                    color: #666;
                    border-color: #ddd;
                    &:hover {
                        background-color: #e0e0e0;
                    }
                `;
        }
    }}
    
    &:hover {
        transform: translateY(-1px);
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
`;

const TagCount = styled.span`
    font-size: 0.75rem;
    opacity: 0.8;
    margin-left: 4px;
`;

const TagSearchInput = styled.input`
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 0.875rem;
    margin-bottom: 12px;
    transition: border-color 0.2s;

    &:focus {
        outline: none;
        border-color: #007bff;
    }
`;

const TagCloudControls = styled.div`
    display: flex;
    gap: 12px;
    align-items: center;
    margin-bottom: 12px;
    flex-wrap: wrap;
`;

const TagCloudControl = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.875rem;
    color: #495057;
`;

const TagCloudToggle = styled.label`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.875rem;
    color: #495057;
    cursor: pointer;
`;

const ToggleInput = styled.input`
    margin: 0;
`;

const PaginationContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 24px;
    padding: 16px;
    background-color: #f8f9fa;
    border-radius: 4px;
    border: 1px solid #e1e1e1;
`;



// Type definitions for filter fields
type ResolutionFields = 'minWidth' | 'maxWidth' | 'minHeight' | 'maxHeight';
type DurationFields = 'min' | 'max';

// Main component
const SearchPage: React.FC<SearchPageProps> = ({ projectId, onSelect, isComponent = false }) => {
    // State management
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<AssetMetadata[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(getDefaultPageSize('SEARCH_RESULTS'));
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [currentItemCount, setCurrentItemCount] = useState(0);

    // Filter state
    const [hasFilterChanges, setHasFilterChanges] = useState(false);
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    // Enhanced filter state with resolution and duration
    const [filters, setFilters] = useState({
        workspace_id: '', // Empty default - no hard-coded values
        project_id: projectId || '', // Use provided projectId or empty
        account_id: '', // Empty default
        account_status_id: '', // Empty default
        created_user_id: '', // Empty default
        media_type: '',
        tags: [] as string[],
        resolution: {
            minWidth: undefined as number | undefined,
            maxWidth: undefined as number | undefined,
            minHeight: undefined as number | undefined,
            maxHeight: undefined as number | undefined,
        },
        duration: {
            min: undefined as number | undefined,
            max: undefined as number | undefined,
        }
    });

    // Tag cloud state
    const [tagCloud, setTagCloud] = useState<ExternalSystemTagCloudResponse | null>(null);
    const [tagCloudLoading, setTagCloudLoading] = useState(false);
    const [selectedTagCloudTags, setSelectedTagCloudTags] = useState<string[]>([]);

    // Input state
    const [tagsInput, setTagsInput] = useState('');
    const [selectedMediaTypes, setSelectedMediaTypes] = useState<string[]>([]);

    // Tag cloud demo features
    const [tagSearchText, setTagSearchText] = useState('');
    const [tagSearchMode, setTagSearchMode] = useState<'AND' | 'OR'>('OR');
    const [tagOrder, setTagOrder] = useState<'POPULARITY' | 'PRIORITY' | 'ALPHABETICAL' | 'COUNT'>('POPULARITY');
    const [normalizeTags, setNormalizeTags] = useState(true);
    const [useTagFiltering, setUseTagFiltering] = useState(false);
    const [tagSearchInputValue, setTagSearchInputValue] = useState(''); // Separate state for input value

    // Prefix matching state - Default to true for better UX
    const [usePrefixMatching, setUsePrefixMatching] = useState(true);

    // Sorting state 
    const [sortBy, setSortBy] = useState<'RELEVANCE' | 'UPDATED_AT' | 'NAME'>('RELEVANCE');
    const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('DESC');

    // Tag cloud cache to prevent excessive API calls
    const [tagCloudCache, setTagCloudCache] = useState<Map<string, ExternalSystemTagCloudResponse>>(new Map());

    // Abort controller for tag cloud requests
    const tagCloudAbortController = useRef<AbortController | null>(null);

    // Abort controller for search requests to prevent race conditions
    const searchAbortController = useRef<AbortController | null>(null);

    // Predefined media types
    const predefinedMediaTypes = [
        { value: 'image', label: 'Image' },
        { value: 'video', label: 'Video' },
        { value: 'audio', label: 'Audio' },
        { value: 'document', label: 'Document' },
        { value: 'model_3d', label: '3D Model' },
        { value: 'adobe', label: 'Adobe' },
        { value: 'other', label: 'Other' }
    ];

    // Helper function to convert ExternalSystemAssetResponse to AssetMetadata
    const convertToAssetMetadata = useCallback((externalAsset: ExternalSystemAssetResponse): AssetMetadata => {
        // Parse the metadata JSON string if it exists
        let parsedMetadata: Record<string, any> = {};
        if (externalAsset.metadata) {
            try {
                parsedMetadata = JSON.parse(externalAsset.metadata);
            } catch (e) {
                console.warn('Failed to parse metadata JSON:', e);
                parsedMetadata = {};
            }
        }

        return {
            id: externalAsset.id,
            title: externalAsset.title || externalAsset.name,
            description: externalAsset.description || '',
            asset_type: externalAsset.media_type,
            image_url: externalAsset.thumbnail_url || externalAsset.url,
            sas_url: externalAsset.sas_url || externalAsset.url,
            file_path: '',
            file_size: externalAsset.file_size || externalAsset.size,
            mime_type: externalAsset.mime_type || '',
            tags: externalAsset.tags || [],
            folder_tags: externalAsset.folder_tags || [],
            project_id: externalAsset.project_id,
            created_at: externalAsset.created_at,
            updated_at: externalAsset.updated_at,
            indexed_at: externalAsset.indexed_at,
            metadata: {
                // Include all the parsed metadata
                ...parsedMetadata,
                // Include basic fields as well
                workspace_id: externalAsset.workspace_id,
                account_id: externalAsset.account_id,
                account_status_id: externalAsset.account_status_id,
                created_user_id: externalAsset.created_user_id,
                width: externalAsset.width,
                height: externalAsset.height,
                duration: externalAsset.duration,
                resolution: externalAsset.resolution,
                orientation: externalAsset.orientation,
                frame_rate: externalAsset.frame_rate,
                has_standardized_metadata: externalAsset.has_standardized_metadata,
                content_type: externalAsset.content_type,
                asset_type: externalAsset.asset_type,
                comment_count: externalAsset.comment_count,
                max_comment_depth: externalAsset.max_comment_depth,
                latest_comment_date: externalAsset.latest_comment_date,
                indexed_at: externalAsset.indexed_at,
                preview_url: externalAsset.preview_url
            }
        };
    }, []);

    // Core search function using external system API
    const handleSearch = useCallback(async (page: number = 1, size: number = pageSize) => {
        if (!query.trim()) {
            setResults([]);
            setTotalItems(0);
            setTotalPages(0);
            setCurrentItemCount(0);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Build external system search request
            const searchRequest: ExternalSystemSearchRequest = {
                query: query.trim(),
                // Explicit account scope (required by the backend)
                accountId: filters.account_id && filters.account_id.trim() ? filters.account_id.trim() : undefined,
                normalizeTags: normalizeTags,
                usePrefixMatching: usePrefixMatching,
                includeTagCloud: false,
                filter: {
                    mediaType: selectedMediaTypes.length > 0 ? selectedMediaTypes : undefined,
                    workspaceId: filters.workspace_id ? filters.workspace_id.split(',').map(id => id.trim()).filter(id => id.length > 0) : undefined,
                    projectId: filters.project_id ? filters.project_id.split(',').map(id => id.trim()).filter(id => id.length > 0) : undefined,
                    accountStatusId: filters.account_status_id ? filters.account_status_id.split(',').map(id => id.trim()).filter(id => id.length > 0) : undefined,
                    tags: selectedTagCloudTags.length > 0 ? selectedTagCloudTags : undefined,
                    userId: filters.created_user_id ? filters.created_user_id.split(',').map(id => id.trim()).filter(id => id.length > 0) : undefined,
                    resolution: {
                        minWidth: filters.resolution.minWidth,
                        maxWidth: filters.resolution.maxWidth,
                        minHeight: filters.resolution.minHeight,
                        maxHeight: filters.resolution.maxHeight,
                    },
                    duration: {
                        min: filters.duration.min,
                        max: filters.duration.max,
                    }
                },
                sort: {
                    by: sortBy,
                    direction: sortDirection
                },
                tagSearchMode: tagSearchMode,
                pagination: {
                    pageIndex: page - 1,
                    itemsPerPage: size
                }
            };

            // Debug logging
            console.log('🔍 Search Request:', searchRequest);
            console.log('🔍 Selected Tags:', selectedTagCloudTags);
            console.log('🔍 Filters:', filters);

            // Execute search
            const response = await searchAssetsExternalSystem(searchRequest);

            // Debug logging
            console.log('🔍 Search Response:', response);
            console.log('🔍 Response Type:', typeof response);
            console.log('🔍 Response Keys:', Object.keys(response));
            console.log('🔍 Response JSON:', JSON.stringify(response, null, 2));

            // Validate response structure
            if (!response || typeof response !== 'object') {
                throw new Error(`Invalid response format: expected object, got ${typeof response}`);
            }

            if (!response.items || !Array.isArray(response.items)) {
                console.error('🔍 Invalid response structure:', response);
                throw new Error(`Invalid response structure: missing or invalid 'items' array. Response: ${JSON.stringify(response)}`);
            }

            console.log('🔍 Response Items Length:', response.items.length);

            // Convert external system response to AssetMetadata format
            const convertedResults = response.items.map(convertToAssetMetadata);
            console.log('🔍 Converted Results:', convertedResults);

            setResults(convertedResults);
            setCurrentPage(page);
            setTotalItems(response.total_items);
            setTotalPages(response.total_pages);
            setCurrentItemCount(response.current_item_count);

        } catch (err) {
            setError('Failed to fetch search results. Please try again.');
            console.error('Search error:', err);
        } finally {
            setLoading(false);
        }
    }, [query, pageSize, selectedMediaTypes, filters, normalizeTags, tagSearchMode, convertToAssetMetadata, sortBy, sortDirection, usePrefixMatching]);

    // NEW: Handle search with explicit selected tags to avoid stale state
    const handleSearchWithSelectedTags = useCallback(async (page: number, size: number, explicitSelectedTags: string[]) => {
        if (!query.trim()) {
            setResults([]);
            setTotalItems(0);
            setTotalPages(0);
            setCurrentItemCount(0);
            return;
        }

        // Check if this request has been aborted
        if (searchAbortController.current?.signal.aborted) {
            console.log('🔍 Search request was aborted, skipping execution');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Build external system search request with explicit tags
            const searchRequest: ExternalSystemSearchRequest = {
                query: query.trim(),
                // Explicit account scope (required by the backend)
                accountId: filters.account_id && filters.account_id.trim() ? filters.account_id.trim() : undefined,
                normalizeTags: normalizeTags,
                usePrefixMatching: usePrefixMatching,
                includeTagCloud: false,
                filter: {
                    mediaType: selectedMediaTypes.length > 0 ? selectedMediaTypes : undefined,
                    workspaceId: filters.workspace_id ? filters.workspace_id.split(',').map(id => id.trim()).filter(id => id.length > 0) : undefined,
                    projectId: filters.project_id ? filters.project_id.split(',').map(id => id.trim()).filter(id => id.length > 0) : undefined,
                    accountStatusId: filters.account_status_id ? filters.account_status_id.split(',').map(id => id.trim()).filter(id => id.length > 0) : undefined,
                    tags: explicitSelectedTags.length > 0 ? explicitSelectedTags : undefined, // Use explicit tags
                    userId: filters.created_user_id ? filters.created_user_id.split(',').map(id => id.trim()).filter(id => id.length > 0) : undefined,
                    resolution: {
                        minWidth: filters.resolution.minWidth,
                        maxWidth: filters.resolution.maxWidth,
                        minHeight: filters.resolution.minHeight,
                        maxHeight: filters.resolution.maxHeight,
                    },
                    duration: {
                        min: filters.duration.min,
                        max: filters.duration.max,
                    }
                },
                sort: {
                    by: sortBy,
                    direction: sortDirection
                },
                tagSearchMode: tagSearchMode,
                pagination: {
                    pageIndex: page - 1,
                    itemsPerPage: size
                }
            };

            // Debug logging
            console.log('🔍 Search with Explicit Tags Request:', searchRequest);
            console.log('🔍 Explicit Selected Tags:', explicitSelectedTags);
            console.log('🔍 Search Request JSON:', JSON.stringify(searchRequest, null, 2));

            // Execute search with abort signal
            const response = await searchAssetsExternalSystem(searchRequest);

            // Check again if request was aborted after completion
            if (searchAbortController.current?.signal.aborted) {
                console.log('🔍 Search completed but was aborted, ignoring results');
                return;
            }

            // Debug logging
            console.log('🔍 Search with Explicit Tags Response:', response);
            console.log('🔍 Response Type:', typeof response);
            console.log('🔍 Response Keys:', Object.keys(response));
            console.log('🔍 Response JSON:', JSON.stringify(response, null, 2));

            // Validate response structure
            if (!response || typeof response !== 'object') {
                throw new Error(`Invalid response format: expected object, got ${typeof response}`);
            }

            if (!response.items || !Array.isArray(response.items)) {
                console.error('🔍 Invalid response structure:', response);
                throw new Error(`Invalid response structure: missing or invalid 'items' array. Response: ${JSON.stringify(response)}`);
            }

            console.log('🔍 Response Items Length:', response.items.length);

            // Convert external system response to AssetMetadata format
            const convertedResults = response.items.map(convertToAssetMetadata);
            console.log('🔍 Converted Results:', convertedResults);

            setResults(convertedResults);
            setCurrentPage(page);
            setTotalItems(response.total_items);
            setTotalPages(response.total_pages);
            setCurrentItemCount(response.current_item_count);

        } catch (err) {
            // Don't show error for aborted requests
            if (err instanceof Error && err.name === 'AbortError') {
                console.log('🔍 Search request was aborted');
                return;
            }

            // Check if request was aborted during error handling
            if (searchAbortController.current?.signal.aborted) {
                console.log('🔍 Search error occurred but request was aborted, ignoring');
                return;
            }

            setError('Failed to fetch search results. Please try again.');
            console.error('Search error:', err);
        } finally {
            // Only update loading state if request wasn't aborted
            if (!searchAbortController.current?.signal.aborted) {
                setLoading(false);
            }
        }
    }, [query, selectedMediaTypes, filters, normalizeTags, tagSearchMode, convertToAssetMetadata, sortBy, sortDirection, usePrefixMatching, pageSize]);

    // EXPERT FIX: Fetch tag cloud with explicit selected tags parameter
    const fetchTagCloud = useCallback(async (explicitSelectedTags?: string[]) => {
        if (!query.trim()) return;

        // Use explicit tags if provided, otherwise use current state
        const tagsToUse = explicitSelectedTags !== undefined ? explicitSelectedTags : selectedTagCloudTags;

        // Cancel any existing request
        if (tagCloudAbortController.current) {
            tagCloudAbortController.current.abort();
        }

        // Create new abort controller
        tagCloudAbortController.current = new AbortController();

        // Check cache first with explicit tags
        const cacheKey = JSON.stringify({
            query,
            tagSearchText,
            tagSearchMode,
            tagOrder,
            normalizeTags,
            usePrefixMatching,
            selectedTagCloudTags: tagsToUse, // Use explicit tags for cache key
            filters: {
                mediaType: selectedMediaTypes,
                workspaceId: filters.workspace_id,
                projectId: filters.project_id,
                accountStatusId: filters.account_status_id,
                userId: filters.created_user_id,
                resolution: filters.resolution,
                duration: filters.duration
            }
        });

        const cachedResult = tagCloudCache.get(cacheKey);
        if (cachedResult) {
            setTagCloud(cachedResult);
            return;
        }

        setTagCloudLoading(true);

        try {
            const tagCloudRequest: ExternalSystemTagCloudRequest = {
                searchText: query.trim(),
                tagSearchText: tagSearchText || undefined,
                tagSearchMode,
                tagOrder,
                // Explicit account scope (required by the backend)
                accountId: filters.account_id && filters.account_id.trim() ? filters.account_id.trim() : undefined,
                usePrefixMatching: usePrefixMatching,
                filter: {
                    mediaType: selectedMediaTypes.length > 0 ? selectedMediaTypes : undefined,
                    workspaceId: filters.workspace_id ? filters.workspace_id.split(',').map(id => id.trim()).filter(id => id.length > 0) : undefined,
                    projectId: filters.project_id ? filters.project_id.split(',').map(id => id.trim()).filter(id => id.length > 0) : undefined,
                    accountStatusId: filters.account_status_id ? filters.account_status_id.split(',').map(id => id.trim()).filter(id => id.length > 0) : undefined,
                    tags: tagsToUse.length > 0 ? tagsToUse : undefined, // EXPERT FIX: Use explicit tags for prioritization
                    userId: filters.created_user_id ? filters.created_user_id.split(',').map(id => id.trim()).filter(id => id.length > 0) : undefined,
                    resolution: {
                        minWidth: filters.resolution.minWidth,
                        maxWidth: filters.resolution.maxWidth,
                        minHeight: filters.resolution.minHeight,
                        maxHeight: filters.resolution.maxHeight,
                    },
                    duration: {
                        min: filters.duration.min,
                        max: filters.duration.max,
                    }
                },
                pagination: {
                    pageIndex: 0,
                    itemsPerPage: getDefaultPageSize('TAG_CLOUD')
                },
                normalizeTags
            };

            // Debug logging for tag cloud request
            console.log('🏷️ Tag Cloud Request Debug:', {
                explicitSelectedTags,
                tagsToUse,
                tagCloudRequest: JSON.stringify(tagCloudRequest, null, 2)
            });

            // EXPERT DEBUG: Log the actual URL being sent
            const debugUrl = new URL('http://localhost:8003/api/external-system/tag-cloud');
            debugUrl.searchParams.append('searchText', query.trim());
            if (tagsToUse.length > 0) {
                tagsToUse.forEach((tag, index) => {
                    debugUrl.searchParams.append(`filter[tags][${index}]`, tag);
                });
            }
            debugUrl.searchParams.append('pagination[pageIndex]', '0');
            debugUrl.searchParams.append('pagination[itemsPerPage]', '50');
            console.log('🏷️ Tag Cloud URL Debug:', debugUrl.toString());

            const tagCloudResponse = await getTagCloudExternalSystem(tagCloudRequest);

            // Check if request was cancelled
            if (tagCloudAbortController.current?.signal.aborted) {
                return;
            }

            // Debug logging for tag cloud response
            console.log('🏷️ Tag Cloud Response Debug:', {
                responseItems: tagCloudResponse.items?.length || 0,
                firstFewTags: tagCloudResponse.items?.slice(0, 5).map(item => ({ value: item.value, count: item.count })) || []
            });

            // Cache the result
            setTagCloudCache(prev => {
                const newCache = new Map(prev);
                newCache.set(cacheKey, tagCloudResponse);
                // Limit cache size
                if (newCache.size > 20) {
                    const firstKey = newCache.keys().next().value;
                    if (firstKey !== undefined) {
                        newCache.delete(firstKey);
                    }
                }
                return newCache;
            });

            setTagCloud(tagCloudResponse);
        } catch (err) {
            if (err instanceof Error && err.name === 'AbortError') {
                return; // Request was cancelled
            }
            console.error('Tag cloud error:', err);
        } finally {
            setTagCloudLoading(false);
        }
    }, [query, tagSearchText, tagSearchMode, tagOrder, normalizeTags, usePrefixMatching, useTagFiltering, selectedTagCloudTags, selectedMediaTypes, filters, tagCloudCache]);

    // REMOVED: Debounced tag cloud fetching - now only triggered on explicit user actions

    // Remove debounced tag search - only search on Enter or button click
    // const debouncedTagSearch = useCallback(
    //     debounce((searchText: string) => {
    //         setTagSearchText(searchText);
    //     }, 500),
    //     []
    // );

    // REMOVED: Debounced tag selection search - replaced with expert sequential approach
    // The new approach ensures proper sequencing and prevents race conditions

    // Event handlers
    const handleFormSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        handleSearchClick();
    };

    const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSearchClick();
        }
    };

    const handleSearchClick = () => {
        if (query.trim()) {
            // EXPERT FIX: Use sequential approach for search button click
            // Cancel any pending requests to prevent race conditions
            if (searchAbortController.current) {
                searchAbortController.current.abort();
            }
            if (tagCloudAbortController.current) {
                tagCloudAbortController.current.abort();
            }

            // Create new abort controllers
            searchAbortController.current = new AbortController();
            tagCloudAbortController.current = new AbortController();

            // EXPERT SEQUENCING: First trigger search, then refresh tag cloud
            const executeSequentialRequests = async () => {
                try {
                    // Step 1: Execute search with current selected tags
                    await handleSearchWithSelectedTags(1, pageSize, selectedTagCloudTags);

                    // Step 2: Only refresh tag cloud if search was successful and not aborted
                    if (!searchAbortController.current?.signal.aborted) {
                        await fetchTagCloud(selectedTagCloudTags); // EXPERT FIX: Pass explicit selected tags
                    }
                } catch (error) {
                    console.error('Sequential search click error:', error);
                }
            };

            // Execute with small delay to ensure state is updated
            setTimeout(executeSequentialRequests, 50);
            setHasFilterChanges(false);
        }
    };

    const handlePageSizeChange = (newSize: number) => {
        setPageSize(newSize);
        // Use handleSearchWithSelectedTags to maintain selected tag cloud tags when changing page size
        handleSearchWithSelectedTags(1, newSize, selectedTagCloudTags);
    };

    const handlePageChange = (newPage: number) => {
        // Use handleSearchWithSelectedTags to maintain selected tag cloud tags across pagination
        handleSearchWithSelectedTags(newPage, pageSize, selectedTagCloudTags);
    };

    const handleTagClick = (tag: string) => {
        setQuery(tag);
        handleSearchClick();
    };

    // Filter handlers
    const handleBasicFilterChange = (field: keyof typeof filters, value: string) => {
        setHasFilterChanges(true);
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleTagsChange = (value: string) => {
        setTagsInput(value);
        setHasFilterChanges(true);

        let tags: string[];
        if (value.includes(',')) {
            tags = value
                .split(',')
                .map(tag => tag.trim())
                .filter(tag => tag.length > 0);
        } else {
            const trimmedValue = value.trim();
            tags = trimmedValue.length > 0 ? [trimmedValue] : [];
        }

        setFilters(prev => ({
            ...prev,
            tags
        }));
    };

    const handleMediaTypeSelect = (mediaType: string) => {
        if (!selectedMediaTypes.includes(mediaType)) {
            const newSelectedTypes = [...selectedMediaTypes, mediaType];
            setSelectedMediaTypes(newSelectedTypes);
            setHasFilterChanges(true);
        }
    };

    const handleRemoveMediaType = (mediaTypeToRemove: string) => {
        const newSelectedTypes = selectedMediaTypes.filter(type => type !== mediaTypeToRemove);
        setSelectedMediaTypes(newSelectedTypes);
        setHasFilterChanges(true);
    };

    const handleResolutionChange = (field: ResolutionFields, value: string) => {
        setHasFilterChanges(true);
        setFilters(prev => ({
            ...prev,
            resolution: {
                ...prev.resolution,
                [field]: value === '' ? undefined : parseInt(value) || undefined
            }
        }));
    };

    const handleDurationChange = (field: DurationFields, value: string) => {
        setHasFilterChanges(true);
        setFilters(prev => ({
            ...prev,
            duration: {
                ...prev.duration,
                [field]: value === '' ? undefined : parseInt(value) || undefined
            }
        }));
    };

    const handleApplyFilters = () => {
        if (query.trim()) {
            // EXPERT FIX: Use sequential approach for apply filters
            // Cancel any pending requests to prevent race conditions
            if (searchAbortController.current) {
                searchAbortController.current.abort();
            }
            if (tagCloudAbortController.current) {
                tagCloudAbortController.current.abort();
            }

            // Create new abort controllers
            searchAbortController.current = new AbortController();
            tagCloudAbortController.current = new AbortController();

            // EXPERT SEQUENCING: First trigger search, then refresh tag cloud
            const executeSequentialRequests = async () => {
                try {
                    // Step 1: Execute search with current selected tags
                    await handleSearchWithSelectedTags(1, pageSize, selectedTagCloudTags);

                    // Step 2: Only refresh tag cloud if search was successful and not aborted
                    if (!searchAbortController.current?.signal.aborted) {
                        await fetchTagCloud(selectedTagCloudTags); // EXPERT FIX: Pass explicit selected tags
                    }
                } catch (error) {
                    console.error('Sequential apply filters error:', error);
                }
            };

            // Execute with small delay to ensure state is updated
            setTimeout(executeSequentialRequests, 50);
            setHasFilterChanges(false);
        }
    };

    const handleResetFilters = () => {
        setFilters(prev => ({
            ...prev,
            workspace_id: '', // Empty default
            project_id: projectId || '', // Use provided projectId or empty
            account_id: '', // Empty default
            account_status_id: '', // Empty default
            created_user_id: '', // Empty default
            media_type: '',
            tags: [],
            resolution: {
                minWidth: undefined,
                maxWidth: undefined,
                minHeight: undefined,
                maxHeight: undefined
            },
            duration: {
                min: undefined,
                max: undefined
            }
        }));
        setTagsInput('');
        setSelectedMediaTypes([]);
        setSelectedTagCloudTags([]);
        setTagSearchInputValue(''); // Clear tag search input
        setTagSearchText(''); // Clear tag search text
        setHasFilterChanges(true);
    };

    // EXPERT FIX: Tag cloud handlers with proper sequencing and tag cloud refresh
    const handleTagCloudTagClick = useCallback((tag: { value: string; id?: string }) => {
        const tagValue = tag.value;
        const isCurrentlySelected = selectedTagCloudTags.includes(tagValue);

        const newSelectedTags = isCurrentlySelected
            ? selectedTagCloudTags.filter(t => t !== tagValue)
            : [...selectedTagCloudTags, tagValue];

        // Debug logging
        console.log('🏷️ Tag Click Debug:', {
            tagValue,
            isCurrentlySelected,
            currentSelectedTags: selectedTagCloudTags,
            newSelectedTags,
            query: query.trim()
        });

        // Update state immediately
        setSelectedTagCloudTags(newSelectedTags);

        // EXPERT FIX: Trigger both search AND tag cloud refresh with proper sequencing
        if (query.trim()) {
            // Cancel any pending requests to prevent race conditions
            if (searchAbortController.current) {
                searchAbortController.current.abort();
            }
            if (tagCloudAbortController.current) {
                tagCloudAbortController.current.abort();
            }

            // Create new abort controllers
            searchAbortController.current = new AbortController();
            tagCloudAbortController.current = new AbortController();

            // EXPERT SEQUENCING: First trigger search, then refresh tag cloud
            // This ensures consistent state and prevents race conditions
            const executeSequentialRequests = async () => {
                try {
                    // Step 1: Execute search with new selected tags
                    await handleSearchWithSelectedTags(1, pageSize, newSelectedTags);

                    // Step 2: Only refresh tag cloud if search was successful and not aborted
                    if (!searchAbortController.current?.signal.aborted) {
                        await fetchTagCloud(newSelectedTags); // EXPERT FIX: Pass explicit selected tags
                    }
                } catch (error) {
                    console.error('Sequential request error:', error);
                }
            };

            // Execute with small delay to ensure state is updated
            setTimeout(executeSequentialRequests, 50);
        }
    }, [selectedTagCloudTags, query, pageSize, handleSearchWithSelectedTags, fetchTagCloud]);

    const clearSelectedTagCloudTags = useCallback(() => {
        setSelectedTagCloudTags([]);
        if (query.trim()) {
            // EXPERT FIX: Use same sequential approach for clearing tags
            // Cancel any pending requests to prevent race conditions
            if (searchAbortController.current) {
                searchAbortController.current.abort();
            }
            if (tagCloudAbortController.current) {
                tagCloudAbortController.current.abort();
            }

            // Create new abort controllers
            searchAbortController.current = new AbortController();
            tagCloudAbortController.current = new AbortController();

            // EXPERT SEQUENCING: First trigger search, then refresh tag cloud
            const executeSequentialRequests = async () => {
                try {
                    // Step 1: Execute search with empty selected tags
                    await handleSearchWithSelectedTags(1, pageSize, []);

                    // Step 2: Only refresh tag cloud if search was successful and not aborted
                    if (!searchAbortController.current?.signal.aborted) {
                        await fetchTagCloud([]); // EXPERT FIX: Pass explicit empty selected tags
                    }
                } catch (error) {
                    console.error('Sequential clear request error:', error);
                }
            };

            // Execute with small delay to ensure state is updated
            setTimeout(executeSequentialRequests, 50);
        }
    }, [query, pageSize, handleSearchWithSelectedTags, fetchTagCloud]);

    // Handle tag search input changes (no API calls)
    const handleTagSearchInputChange = (value: string) => {
        setTagSearchInputValue(value);
    };

    // Handle tag search submission (Enter key or button click)
    const handleTagSearchSubmit = () => {
        const currentSearchText = tagSearchInputValue.trim();
        if (!currentSearchText) return; // Don't search if input is empty

        console.log('🔍 Tag Search Submit:', {
            currentSearchText,
            query: query.trim(),
            tagSearchMode,
            tagOrder,
            normalizeTags
        });

        setTagSearchText(currentSearchText);

        // Trigger tag cloud fetch with new search text immediately
        if (query.trim()) {
            // Create a new request with the current search text
            const tagCloudRequest: ExternalSystemTagCloudRequest = {
                searchText: query.trim(),
                tagSearchText: currentSearchText,
                tagSearchMode,
                tagOrder,
                // Explicit account scope (required by the backend)
                accountId: filters.account_id && filters.account_id.trim() ? filters.account_id.trim() : undefined,
                usePrefixMatching: usePrefixMatching,
                filter: {
                    mediaType: selectedMediaTypes.length > 0 ? selectedMediaTypes : undefined,
                    workspaceId: filters.workspace_id ? filters.workspace_id.split(',').map(id => id.trim()).filter(id => id.length > 0) : undefined,
                    projectId: filters.project_id ? filters.project_id.split(',').map(id => id.trim()).filter(id => id.length > 0) : undefined,
                    accountStatusId: filters.account_status_id ? filters.account_status_id.split(',').map(id => id.trim()).filter(id => id.length > 0) : undefined,
                    tags: selectedTagCloudTags.length > 0 ? selectedTagCloudTags : undefined,
                    userId: filters.created_user_id ? filters.created_user_id.split(',').map(id => id.trim()).filter(id => id.length > 0) : undefined,
                    resolution: {
                        minWidth: filters.resolution.minWidth,
                        maxWidth: filters.resolution.maxWidth,
                        minHeight: filters.resolution.minHeight,
                        maxHeight: filters.resolution.maxHeight,
                    },
                    duration: {
                        min: filters.duration.min,
                        max: filters.duration.max,
                    }
                },
                pagination: {
                    pageIndex: 0,
                    itemsPerPage: getDefaultPageSize('TAG_CLOUD')
                },
                normalizeTags
            };

            console.log('🔍 Tag Cloud Request:', tagCloudRequest);

            // Cancel any existing request
            if (tagCloudAbortController.current) {
                tagCloudAbortController.current.abort();
            }

            // Create new abort controller
            tagCloudAbortController.current = new AbortController();

            setTagCloudLoading(true);

            // Execute the tag cloud request directly
            getTagCloudExternalSystem(tagCloudRequest)
                .then((tagCloudResponse) => {
                    // Check if request was cancelled
                    if (tagCloudAbortController.current?.signal.aborted) {
                        return;
                    }

                    console.log('🔍 Tag Cloud Response:', tagCloudResponse);

                    // Update the tag cloud state
                    setTagCloud(tagCloudResponse);
                })
                .catch((err) => {
                    if (err instanceof Error && err.name === 'AbortError') {
                        return; // Request was cancelled
                    }
                    console.error('Tag cloud error:', err);
                })
                .finally(() => {
                    setTagCloudLoading(false);
                });
        }
    };

    // Handle Enter key in tag search input
    const handleTagSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleTagSearchSubmit();
        }
    };

    // Effects

    useEffect(() => {
        if (!query.trim()) {
            // Clear results when query is empty
            setResults([]);
            setTotalItems(0);
            setTotalPages(0);
            setCurrentItemCount(0);
            setTagCloud(null);
            setSelectedTagCloudTags([]);
        }
    }, [query]);

    // Reset selected tags when core filters change (workspace, project, account)
    // Don't reset for resolution, duration, or media type changes as these don't affect tag relevance
    useEffect(() => {
        setSelectedTagCloudTags([]);
    }, [filters.workspace_id, filters.project_id, filters.account_id, filters.account_status_id]);

    // Sync selected media types with filters
    useEffect(() => {
        if (filters.media_type) {
            const mediaTypes = filters.media_type.split(',').map(t => t.trim()).filter(t => t.length > 0);
            setSelectedMediaTypes(mediaTypes);
        } else {
            setSelectedMediaTypes([]);
        }
    }, [filters.media_type]);

    // REMOVED: Automatic tag cloud fetching on query change
    // Tag cloud will now only be fetched when:
    // 1. Search button is clicked
    // 2. Enter key is pressed in search input
    // 3. Apply filters button is clicked

    // Cleanup effect
    useEffect(() => {
        return () => {
            if (tagCloudAbortController.current) {
                tagCloudAbortController.current.abort();
            }
            if (searchAbortController.current) {
                searchAbortController.current.abort();
            }
        };
    }, []);

    // Handle asset deletion
    const handleDeleteAsset = (assetId: string) => {
        setResults(prevResults => prevResults.filter(asset => asset.id !== assetId));
        setTotalItems(prev => Math.max(0, prev - 1));
    };

    // Render functions
    const renderAssetCard = (asset: AssetMetadata) => {
        if (isComponent) {
            return (
                <div key={asset.id} onClick={() => onSelect && onSelect(asset)} style={{
                    border: '1px solid #e1e1e1',
                    borderRadius: '8px',
                    padding: '16px',
                    background: 'white',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    cursor: 'pointer'
                }}>
                    <img
                        src={asset.image_url || asset.sas_url || PLACEHOLDER_IMAGE}
                        alt={asset.title || 'Asset image'}
                        style={{
                            width: '100%',
                            height: '200px',
                            objectFit: 'cover',
                            borderRadius: '4px',
                            marginBottom: '12px'
                        }}
                        onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = PLACEHOLDER_IMAGE;
                        }}
                    />
                    <h3 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '8px' }}>
                        {asset.title || 'Untitled Asset'}
                    </h3>
                    {asset.description && (
                        <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '12px' }}>
                            {asset.description}
                        </p>
                    )}
                    {asset.tags && asset.tags.length > 0 && (
                        <div style={{ marginTop: '12px' }}>
                            <div style={{ fontSize: '0.75rem', color: '#999', marginBottom: '4px', fontWeight: '500' }}>
                                🤖 AI Tags:
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {asset.tags.map((tag: string) => (
                                    <button
                                        key={tag}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleTagClick(tag);
                                        }}
                                        style={{
                                            padding: '4px 8px',
                                            borderRadius: '16px',
                                            backgroundColor: '#f3e5f5',
                                            color: '#7b1fa2',
                                            border: '1px solid #e1bee7',
                                            cursor: 'pointer',
                                            fontSize: '0.8rem'
                                        }}
                                    >
                                        {tag}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            );
        }
        return <AssetCard key={asset.id} asset={asset} onDelete={handleDeleteAsset} />;
    };

    const renderTagCloud = () => {
        // Always show the tag cloud container when there's a query, even if no tags found
        if (!query.trim()) return null;

        return (
            <TagCloudContainer>
                <TagCloudTitle>
                    🔗 Tag Cloud
                    {tagCloud && tagCloud.items && tagCloud.items.length > 0 && (
                        <span> ({tagCloud.items.length} tags)</span>
                    )}
                    {tagCloudLoading && <span style={{ fontSize: '0.875rem', color: '#666' }}>(Loading...)</span>}
                </TagCloudTitle>

                {/* Tag Cloud Demo Features - Always visible */}
                <div style={{
                    marginBottom: '16px',
                    padding: '12px',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px'
                }}>
                    <h4 style={{ margin: '0 0 12px 0', fontSize: '0.9rem', color: '#495057' }}>
                        🎯 External System Demo Features
                    </h4>

                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <TagSearchInput
                            type="text"
                            value={tagSearchInputValue}
                            onChange={(e) => handleTagSearchInputChange(e.target.value)}
                            onKeyPress={handleTagSearchKeyPress}
                            placeholder="Search through tag names (e.g., '3d' to find tags containing '3d') - Press Enter or click Search"
                            style={{ flex: 1 }}
                        />
                        <button
                            onClick={handleTagSearchSubmit}
                            style={{
                                padding: '6px 12px',
                                fontSize: '0.8rem',
                                backgroundColor: '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }}
                        >
                            Search Tags
                        </button>
                        {/* Clear button - always visible when there's a search query */}
                        {query.trim() && (
                            <button
                                onClick={() => {
                                    // EXPERT FIX: Cancel any ongoing tag cloud requests to prevent race conditions
                                    if (tagCloudAbortController.current) {
                                        tagCloudAbortController.current.abort();
                                    }

                                    // Create new abort controller for this request
                                    tagCloudAbortController.current = new AbortController();

                                    // Clear the input and search text
                                    setTagSearchInputValue('');
                                    setTagSearchText('');

                                    // Set loading state
                                    setTagCloudLoading(true);

                                    // EXPERT FIX: Immediately refresh tag cloud with cleared search text
                                    if (query.trim()) {
                                        // Create a new tag cloud request with cleared search text
                                        const tagCloudRequest: ExternalSystemTagCloudRequest = {
                                            searchText: query.trim(),
                                            tagSearchText: undefined, // Explicitly clear the search text
                                            tagSearchMode,
                                            tagOrder,
                                            // Explicit account scope (required by the backend)
                                            accountId: filters.account_id && filters.account_id.trim() ? filters.account_id.trim() : undefined,
                                            normalizeTags: normalizeTags, // Add missing normalizeTags parameter
                                            usePrefixMatching: usePrefixMatching, // Added
                                            filter: {
                                                mediaType: selectedMediaTypes.length > 0 ? selectedMediaTypes : undefined,
                                                workspaceId: filters.workspace_id ? filters.workspace_id.split(',').map(id => id.trim()).filter(id => id.length > 0) : undefined,
                                                projectId: filters.project_id ? filters.project_id.split(',').map(id => id.trim()).filter(id => id.length > 0) : undefined,
                                                accountStatusId: filters.account_status_id ? filters.account_status_id.split(',').map(id => id.trim()).filter(id => id.length > 0) : undefined,
                                                tags: selectedTagCloudTags.length > 0 ? selectedTagCloudTags : undefined,
                                                userId: filters.created_user_id ? filters.created_user_id.split(',').map(id => id.trim()).filter(id => id.length > 0) : undefined,
                                                resolution: {
                                                    minWidth: filters.resolution.minWidth,
                                                    maxWidth: filters.resolution.maxWidth,
                                                    minHeight: filters.resolution.minHeight,
                                                    maxHeight: filters.resolution.maxHeight,
                                                },
                                                duration: {
                                                    min: filters.duration.min,
                                                    max: filters.duration.max,
                                                }
                                            },
                                            pagination: {
                                                pageIndex: 0,
                                                itemsPerPage: 50
                                            }
                                        };

                                        // Execute the tag cloud request directly with abort signal
                                        getTagCloudExternalSystem(tagCloudRequest)
                                            .then(tagCloudResponse => {
                                                // Only update if request wasn't aborted
                                                if (!tagCloudAbortController.current?.signal.aborted) {
                                                    setTagCloud(tagCloudResponse);
                                                    setTagCloudLoading(false);
                                                }
                                            })
                                            .catch(error => {
                                                // Only handle error if request wasn't aborted
                                                if (!tagCloudAbortController.current?.signal.aborted) {
                                                    console.error('Tag cloud fetch error:', error);
                                                    setTagCloudLoading(false);
                                                }
                                            });
                                    }
                                }}
                                style={{
                                    padding: '6px 12px',
                                    fontSize: '0.8rem',
                                    backgroundColor: tagCloudLoading ? '#ffc107' : '#6c757d',
                                    color: tagCloudLoading ? '#000' : 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: tagCloudLoading ? 'wait' : 'pointer',
                                    opacity: tagCloudLoading ? 0.8 : 1
                                }}
                                disabled={tagCloudLoading}
                                title={tagCloudLoading ? "Refreshing tag cloud..." : "Clear tag search and refresh"}
                            >
                                {tagCloudLoading ? 'Refreshing...' : 'Clear'}
                            </button>
                        )}

                        {/* Dedicated Refresh Tag Cloud button - always visible */}
                        <button
                            onClick={() => {
                                // EXPERT FIX: Cancel any ongoing tag cloud requests to prevent race conditions
                                if (tagCloudAbortController.current) {
                                    tagCloudAbortController.current.abort();
                                }

                                // Create new abort controller for this request
                                tagCloudAbortController.current = new AbortController();

                                // Set loading state
                                setTagCloudLoading(true);

                                // Refresh tag cloud with current search context
                                if (query.trim()) {
                                    const tagCloudRequest: ExternalSystemTagCloudRequest = {
                                        searchText: query.trim(),
                                        tagSearchText: tagSearchText || undefined,
                                        tagSearchMode,
                                        tagOrder,
                                        accountId: filters.account_id && filters.account_id.trim() ? filters.account_id.trim() : undefined,
                                        normalizeTags: normalizeTags,
                                        usePrefixMatching: usePrefixMatching, // Added
                                        filter: {
                                            mediaType: selectedMediaTypes.length > 0 ? selectedMediaTypes : undefined,
                                            workspaceId: filters.workspace_id ? filters.workspace_id.split(',').map(id => id.trim()).filter(id => id.length > 0) : undefined,
                                            projectId: filters.project_id ? filters.project_id.split(',').map(id => id.trim()).filter(id => id.length > 0) : undefined,
                                            accountStatusId: filters.account_status_id ? filters.account_status_id.split(',').map(id => id.trim()).filter(id => id.length > 0) : undefined,
                                            tags: selectedTagCloudTags.length > 0 ? selectedTagCloudTags : undefined,
                                            userId: filters.created_user_id ? filters.created_user_id.split(',').map(id => id.trim()).filter(id => id.length > 0) : undefined,
                                            resolution: {
                                                minWidth: filters.resolution.minWidth,
                                                maxWidth: filters.resolution.maxWidth,
                                                minHeight: filters.resolution.minHeight,
                                                maxHeight: filters.resolution.maxHeight,
                                            },
                                            duration: {
                                                min: filters.duration.min,
                                                max: filters.duration.max,
                                            }
                                        },
                                        pagination: {
                                            pageIndex: 0,
                                            itemsPerPage: 50
                                        }
                                    };

                                    // Execute the tag cloud request with abort signal
                                    getTagCloudExternalSystem(tagCloudRequest)
                                        .then(tagCloudResponse => {
                                            if (!tagCloudAbortController.current?.signal.aborted) {
                                                setTagCloud(tagCloudResponse);
                                                setTagCloudLoading(false);
                                            }
                                        })
                                        .catch(error => {
                                            if (!tagCloudAbortController.current?.signal.aborted) {
                                                console.error('Tag cloud refresh error:', error);
                                                setTagCloudLoading(false);
                                            }
                                        });
                                }
                            }}
                            style={{
                                padding: '6px 12px',
                                fontSize: '0.8rem',
                                backgroundColor: tagCloudLoading ? '#28a745' : '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: tagCloudLoading ? 'wait' : 'pointer',
                                opacity: tagCloudLoading ? 0.8 : 1,
                                marginLeft: '8px'
                            }}
                            disabled={tagCloudLoading || !query.trim()}
                            title={tagCloudLoading ? "Refreshing tag cloud..." : "Refresh tag cloud"}
                        >
                            {tagCloudLoading ? 'Refreshing...' : '🔄 Refresh'}
                        </button>
                    </div>

                    {/* Show current tag search text if different from input */}
                    {tagSearchText && tagSearchText !== tagSearchInputValue && (
                        <div style={{
                            marginTop: '8px',
                            padding: '6px 10px',
                            backgroundColor: '#e8f5e8',
                            border: '1px solid #c8e6c9',
                            borderRadius: '4px',
                            fontSize: '0.8rem',
                            color: '#2e7d32'
                        }}>
                            🔍 <strong>Current tag search:</strong> {tagSearchText}
                        </div>
                    )}

                    <TagCloudControls>
                        <TagCloudControl>
                            <span>Tag Search Mode:</span>
                            <FilterSelect
                                value={tagSearchMode}
                                onChange={(e) => setTagSearchMode(e.target.value as 'AND' | 'OR')}
                                style={{ width: 'auto', minWidth: '80px' }}
                            >
                                <option value="OR">OR</option>
                                <option value="AND">AND</option>
                            </FilterSelect>
                        </TagCloudControl>

                        <TagCloudControl>
                            <span>Tag Order:</span>
                            <FilterSelect
                                value={tagOrder}
                                onChange={(e) => setTagOrder(e.target.value as 'POPULARITY' | 'PRIORITY' | 'ALPHABETICAL' | 'COUNT')}
                                style={{ width: 'auto', minWidth: '120px' }}
                            >
                                <option value="POPULARITY">By Popularity</option>
                                <option value="PRIORITY">By Priority</option>
                                <option value="ALPHABETICAL">Alphabetical</option>
                            </FilterSelect>
                        </TagCloudControl>

                        <TagCloudToggle>
                            <ToggleInput
                                type="checkbox"
                                checked={normalizeTags}
                                onChange={(e) => setNormalizeTags(e.target.checked)}
                            />
                            Normalize Tags
                        </TagCloudToggle>

                        <TagCloudToggle>
                            <ToggleInput
                                type="checkbox"
                                checked={useTagFiltering}
                                onChange={(e) => setUseTagFiltering(e.target.checked)}
                            />
                            Use Tag Filtering
                        </TagCloudToggle>
                    </TagCloudControls>
                </div>

                {/* Show message when no tags found */}
                {(!tagCloud || !tagCloud.items || tagCloud.items.length === 0) && !tagCloudLoading && (
                    <div style={{
                        marginBottom: '12px',
                        padding: '12px',
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px',
                        fontSize: '0.875rem',
                        color: '#6c757d',
                        textAlign: 'center'
                    }}>
                        🔍 No tags found for the current search criteria. Try adjusting your search or filters.
                    </div>
                )}

                {selectedTagCloudTags.length > 0 && (
                    <div style={{
                        marginBottom: '12px',
                        padding: '8px 12px',
                        backgroundColor: '#e3f2fd',
                        border: '1px solid #bbdefb',
                        borderRadius: '4px',
                        fontSize: '0.875rem',
                        color: '#1976d2'
                    }}>
                        🔍 <strong>Active Tag Filters:</strong> {selectedTagCloudTags.join(', ')}
                        <span style={{ marginLeft: '8px', fontSize: '0.75rem', opacity: '0.8' }}>
                            (These tags will be used in search filters)
                        </span>
                        <button
                            onClick={clearSelectedTagCloudTags}
                            style={{
                                marginLeft: '12px',
                                padding: '4px 8px',
                                fontSize: '0.75rem',
                                color: '#dc3545',
                                background: 'none',
                                border: '1px solid #dc3545',
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }}
                        >
                            Clear All
                        </button>
                    </div>
                )}

                {/* Only show tag grid when there are tags */}
                {tagCloud && tagCloud.items && tagCloud.items.length > 0 && (
                    <>
                        <TagCloudGrid>
                            {tagCloud.items?.map((tag: ExternalSystemTagCloudItem) => {
                                const isSelected = selectedTagCloudTags.includes(tag.value);

                                return (
                                    <TagCloudTag
                                        key={tag.id || tag.value}
                                        isSelected={isSelected}
                                        tagType="unified"
                                        onClick={() => handleTagCloudTagClick(tag)}
                                        title={`${tag.value} (${tag.count} assets)`}
                                    >
                                        {tag.value}
                                        <TagCount>({tag.count})</TagCount>
                                    </TagCloudTag>
                                );
                            })}
                        </TagCloudGrid>

                        {/* Tag Cloud Pagination */}
                        {tagCloud.total_pages > 1 && (
                            <div style={{ marginTop: '16px', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                                <Pagination
                                    currentPage={tagCloud.page_index + 1}
                                    totalPages={tagCloud.total_pages}
                                    totalItems={tagCloud.total_items}
                                    currentItemCount={tagCloud.current_item_count}
                                    itemsPerPage={tagCloud.items_per_page}
                                    context="TAG_CLOUD"
                                    onPageChange={(page) => {
                                        // Update tag cloud pagination
                                        const tagCloudRequest: ExternalSystemTagCloudRequest = {
                                            searchText: query.trim(),
                                            tagSearchText: tagSearchText || undefined,
                                            tagSearchMode,
                                            tagOrder,
                                            // Explicit account scope (required by the backend)
                                            accountId: filters.account_id && filters.account_id.trim() ? filters.account_id.trim() : undefined,
                                            usePrefixMatching: usePrefixMatching, // Added
                                            filter: {
                                                mediaType: selectedMediaTypes.length > 0 ? selectedMediaTypes : undefined,
                                                workspaceId: filters.workspace_id ? filters.workspace_id.split(',').map(id => id.trim()).filter(id => id.length > 0) : undefined,
                                                projectId: filters.project_id ? filters.project_id.split(',').map(id => id.trim()).filter(id => id.length > 0) : undefined,
                                                accountStatusId: filters.account_status_id ? filters.account_status_id.split(',').map(id => id.trim()).filter(id => id.length > 0) : undefined,
                                                tags: selectedTagCloudTags.length > 0 ? selectedTagCloudTags : undefined,
                                                userId: filters.created_user_id ? filters.created_user_id.split(',').map(id => id.trim()).filter(id => id.length > 0) : undefined,
                                                resolution: {
                                                    minWidth: filters.resolution.minWidth,
                                                    maxWidth: filters.resolution.maxWidth,
                                                    minHeight: filters.resolution.minHeight,
                                                    maxHeight: filters.resolution.maxHeight,
                                                },
                                                duration: {
                                                    min: filters.duration.min,
                                                    max: filters.duration.max,
                                                }
                                            },
                                            pagination: {
                                                pageIndex: page - 1,
                                                itemsPerPage: getDefaultPageSize('TAG_CLOUD')
                                            },
                                            normalizeTags
                                        };

                                        // Fetch new page
                                        getTagCloudExternalSystem(tagCloudRequest)
                                            .then((newTagCloud) => {
                                                setTagCloud(newTagCloud);
                                            })
                                            .catch((err) => {
                                                console.error('Tag cloud pagination error:', err);
                                            });
                                    }}
                                />
                            </div>
                        )}
                    </>
                )}
            </TagCloudContainer>
        );
    };

    return (
        <Container>
            {/* Demo POC Notice */}
            <div style={{
                marginBottom: '16px',
                padding: '12px 16px',
                backgroundColor: '#e3f2fd',
                border: '1px solid #1976d2',
                borderRadius: '6px',
                fontSize: '0.9rem',
                color: '#1976d2',
                fontWeight: '500'
            }}>
                🎯 <strong>Demo POC Interface:</strong> This is a demonstration frontend for the AI Asset Processing System.
                By default, it searches across ALL accounts for demo purposes.
                In production, the external system (Artstash) handles authentication and security filtering.
            </div>

            <SearchContainer>
                <form onSubmit={handleFormSubmit}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <SearchInput
                            type="text"
                            value={query}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
                            onKeyPress={handleSearchKeyPress}
                            placeholder="Search for assets... (Press Enter to search)"
                            style={{ flex: '1', minWidth: '200px' }}
                        />
                        <SearchButton type="submit">
                            Search
                        </SearchButton>
                    </div>

                    {/* Search Options Controls */}
                    <div style={{
                        display: 'flex',
                        gap: '12px',
                        alignItems: 'center',
                        marginTop: '12px',
                        padding: '8px 12px',
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px',
                        fontSize: '0.875rem',
                        flexWrap: 'wrap'
                    }}>
                        {/* Prefix Matching Toggle */}
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '500', color: '#495057', cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={usePrefixMatching}
                                onChange={(e) => setUsePrefixMatching(e.target.checked)}
                                style={{ margin: 0, cursor: 'pointer' }}
                            />
                            <span>Enable Prefix Matching</span>
                            <span style={{ fontSize: '0.75rem', color: '#6c757d', fontStyle: 'italic' }}>
                                (e.g., "file" matches "filename.png")
                            </span>
                        </label>

                        {/* Sorting Controls */}
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '500', color: '#495057' }}>
                            Sort by:
                            <FilterSelect
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value as 'RELEVANCE' | 'UPDATED_AT' | 'NAME')}
                                style={{ width: 'auto', minWidth: '120px' }}
                            >
                                <option value="RELEVANCE">Relevance</option>
                                <option value="UPDATED_AT">Updated Date</option>
                                <option value="NAME">Name</option>
                            </FilterSelect>
                        </label>

                        {sortBy !== 'RELEVANCE' && (
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '500', color: '#495057' }}>
                                Direction:
                                <FilterSelect
                                    value={sortDirection}
                                    onChange={(e) => setSortDirection(e.target.value as 'ASC' | 'DESC')}
                                    style={{ width: 'auto', minWidth: '80px' }}
                                >
                                    <option value="DESC">Descending</option>
                                    <option value="ASC">Ascending</option>
                                </FilterSelect>
                            </label>
                        )}

                        <div style={{
                            fontSize: '0.8rem',
                            color: '#6c757d',
                            fontStyle: 'italic'
                        }}>
                            {sortBy === 'RELEVANCE'
                                ? 'Using Azure Search semantic ranking for best relevance'
                                : `Sorting by ${sortBy === 'UPDATED_AT' ? 'Updated Date' : 'Name'} (${sortDirection})`
                            }
                        </div>
                    </div>

                    {/* User-friendly notice about search behavior */}
                    <div style={{
                        marginTop: '8px',
                        padding: '6px 10px',
                        backgroundColor: '#e8f5e8',
                        border: '1px solid #c8e6c9',
                        borderRadius: '4px',
                        fontSize: '0.8rem',
                        color: '#2e7d32'
                    }}>
                        💡 <strong>Tip:</strong> Press Enter or click Search to fetch results and tag cloud.
                        {sortBy === 'RELEVANCE' && ' Semantic ranking provides the most relevant results by understanding search intent.'}
                    </div>

                    {selectedTagCloudTags.length > 0 && (
                        <div style={{
                            marginTop: '8px',
                            padding: '6px 10px',
                            backgroundColor: '#fff3cd',
                            border: '1px solid #ffeaa7',
                            borderRadius: '4px',
                            fontSize: '0.8rem',
                            color: '#856404'
                        }}>
                            🏷️ <strong>Search will include tags:</strong> {selectedTagCloudTags.join(', ')}
                        </div>
                    )}
                </form>

                {/* Tag Cloud Section - only shown when there's a query */}
                {query.trim() && renderTagCloud()}

                <FilterContainer>
                    <FilterHeader onClick={() => setIsFilterOpen(!isFilterOpen)}>
                        <FilterTitle>Advanced Filters</FilterTitle>
                        <span>{isFilterOpen ? '▼' : '▶'}</span>
                    </FilterHeader>
                    <FilterContent isOpen={isFilterOpen}>
                        <div style={{
                            marginBottom: '16px',
                            padding: '8px 12px',
                            backgroundColor: '#fff3cd',
                            border: '1px solid #ffeaa7',
                            borderRadius: '4px',
                            fontSize: '0.8rem',
                            color: '#856404'
                        }}>
                            💡 <strong>Note:</strong> Leave filter fields empty to search across all values. Enter specific IDs (comma-separated for multiple) to narrow results.
                        </div>

                        <FilterRow>
                            <FilterGroup>
                                <FilterLabel htmlFor="workspace_id">Workspace ID</FilterLabel>
                                <FilterInput
                                    id="workspace_id"
                                    type="text"
                                    value={filters.workspace_id}
                                    onChange={(e) => handleBasicFilterChange('workspace_id', e.target.value)}
                                    placeholder="Enter workspace ID(s) - comma separated for multiple (e.g., 1,2,3)"
                                />
                            </FilterGroup>
                            <FilterGroup>
                                <FilterLabel htmlFor="project_id">Project ID</FilterLabel>
                                <FilterInput
                                    id="project_id"
                                    type="text"
                                    value={filters.project_id}
                                    onChange={(e) => handleBasicFilterChange('project_id', e.target.value)}
                                    placeholder="Enter project ID(s) - comma separated for multiple (e.g., 114,115)"
                                />
                            </FilterGroup>
                            <FilterGroup>
                                <FilterLabel htmlFor="account_id">Account ID (required)</FilterLabel>
                                <FilterInput
                                    id="account_id"
                                    type="text"
                                    value={filters.account_id}
                                    onChange={(e) => handleBasicFilterChange('account_id', e.target.value)}
                                    placeholder="Enter the account ID to search within (e.g., 1)"
                                />
                                <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '4px', fontSize: '0.8rem', color: '#856404' }}>
                                    🔒 <strong>Account scope required:</strong> searches only run within the specified account. Cross-account search has been removed for security.
                                </div>
                            </FilterGroup>
                            <FilterGroup>
                                <FilterLabel htmlFor="created_user_id">Created User ID</FilterLabel>
                                <FilterInput
                                    id="created_user_id"
                                    type="text"
                                    value={filters.created_user_id}
                                    onChange={(e) => handleBasicFilterChange('created_user_id', e.target.value)}
                                    placeholder="Enter created user ID(s) - comma separated for multiple (e.g., 1,2,3)"
                                />
                            </FilterGroup>
                            <FilterGroup>
                                <FilterLabel htmlFor="account_status_id">Account Status ID</FilterLabel>
                                <FilterInput
                                    id="account_status_id"
                                    type="text"
                                    value={filters.account_status_id}
                                    onChange={(e) => handleBasicFilterChange('account_status_id', e.target.value)}
                                    placeholder="Enter account status ID(s) - comma separated for multiple (e.g., 1,3,5)"
                                />
                            </FilterGroup>
                        </FilterRow>

                        <FilterRow>
                            <FilterGroup>
                                <FilterLabel htmlFor="media_type">Media Type</FilterLabel>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <FilterSelect
                                        value=""
                                        onChange={(e) => {
                                            if (e.target.value) {
                                                handleMediaTypeSelect(e.target.value);
                                                e.target.value = '';
                                            }
                                        }}
                                    >
                                        <option value="">Select media type...</option>
                                        {predefinedMediaTypes.map(type => (
                                            <option
                                                key={type.value}
                                                value={type.value}
                                                disabled={selectedMediaTypes.includes(type.value)}
                                            >
                                                {type.label}
                                            </option>
                                        ))}
                                    </FilterSelect>

                                    {selectedMediaTypes.length > 0 && (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                            {selectedMediaTypes.map(mediaType => (
                                                <button
                                                    key={mediaType}
                                                    onClick={() => handleRemoveMediaType(mediaType)}
                                                    style={{
                                                        padding: '4px 8px',
                                                        borderRadius: '16px',
                                                        backgroundColor: '#e3f2fd',
                                                        color: '#1976d2',
                                                        border: '1px solid #bbdefb',
                                                        cursor: 'pointer',
                                                        fontSize: '0.8rem'
                                                    }}
                                                >
                                                    {mediaType} ×
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </FilterGroup>
                            <FilterGroup>
                                <FilterLabel htmlFor="tags">Tags</FilterLabel>
                                <FilterInput
                                    id="tags"
                                    type="text"
                                    value={tagsInput}
                                    onChange={(e) => handleTagsChange(e.target.value)}
                                    placeholder="Enter tags (e.g., '3d model' or 'spider, dragon')"
                                />
                            </FilterGroup>
                        </FilterRow>

                        <FilterRow>
                            <FilterGroup>
                                <FilterLabel>Resolution</FilterLabel>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                    <div>
                                        <FilterLabel htmlFor="minWidth">Min Width</FilterLabel>
                                        <FilterInput
                                            id="minWidth"
                                            type="number"
                                            value={filters.resolution.minWidth?.toString() || ''}
                                            onChange={(e) => handleResolutionChange('minWidth', e.target.value)}
                                            placeholder="Min width"
                                        />
                                    </div>
                                    <div>
                                        <FilterLabel htmlFor="maxWidth">Max Width</FilterLabel>
                                        <FilterInput
                                            id="maxWidth"
                                            type="number"
                                            value={filters.resolution.maxWidth?.toString() || ''}
                                            onChange={(e) => handleResolutionChange('maxWidth', e.target.value)}
                                            placeholder="Max width"
                                        />
                                    </div>
                                    <div>
                                        <FilterLabel htmlFor="minHeight">Min Height</FilterLabel>
                                        <FilterInput
                                            id="minHeight"
                                            type="number"
                                            value={filters.resolution.minHeight?.toString() || ''}
                                            onChange={(e) => handleResolutionChange('minHeight', e.target.value)}
                                            placeholder="Min height"
                                        />
                                    </div>
                                    <div>
                                        <FilterLabel htmlFor="maxHeight">Max Height</FilterLabel>
                                        <FilterInput
                                            id="maxHeight"
                                            type="number"
                                            value={filters.resolution.maxHeight?.toString() || ''}
                                            onChange={(e) => handleResolutionChange('maxHeight', e.target.value)}
                                            placeholder="Max height"
                                        />
                                    </div>
                                </div>
                            </FilterGroup>
                            <FilterGroup>
                                <FilterLabel>Duration (seconds)</FilterLabel>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                    <div>
                                        <FilterLabel htmlFor="minDuration">Min Duration</FilterLabel>
                                        <FilterInput
                                            id="minDuration"
                                            type="number"
                                            value={filters.duration.min?.toString() || ''}
                                            onChange={(e) => handleDurationChange('min', e.target.value)}
                                            placeholder="Min duration"
                                        />
                                    </div>
                                    <div>
                                        <FilterLabel htmlFor="maxDuration">Max Duration</FilterLabel>
                                        <FilterInput
                                            id="maxDuration"
                                            type="number"
                                            value={filters.duration.max?.toString() || ''}
                                            onChange={(e) => handleDurationChange('max', e.target.value)}
                                            placeholder="Max duration"
                                        />
                                    </div>
                                </div>
                            </FilterGroup>
                        </FilterRow>

                        <FilterActions>
                            <FilterButton onClick={handleResetFilters}>
                                Reset Filters
                            </FilterButton>
                            <FilterButton
                                onClick={handleApplyFilters}
                                disabled={!hasFilterChanges}
                            >
                                Apply Filters
                            </FilterButton>
                        </FilterActions>
                    </FilterContent>
                </FilterContainer>
            </SearchContainer>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            {loading ? (
                <Loading>Loading results...</Loading>
            ) : (
                <>
                    <ResultsGrid>
                        {results.map(renderAssetCard)}
                    </ResultsGrid>
                    <PaginationContainer>
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalItems={totalItems}
                            currentItemCount={currentItemCount}
                            itemsPerPage={pageSize}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                            showPageSizeSelector={true}
                            context="SEARCH_RESULTS"
                        />
                    </PaginationContainer>
                </>
            )}
        </Container>
    );
};

export default SearchPage;
